import type {
  CMSCollectionContentCard,
  CMSCollectionContentCardV2,
  CMSCollectionFilter,
  CMSCollectionFilterGroup,
  CMSCollectionGridItem,
  CMSCollectionSortBy,
  CMSProductCardItem,
  CMSSortBy,
} from '@/types/sanity'
import { isProductCardVariantEnabled } from './commerce'

export const isActiveProductCard = (
  item:
    | CMSProductCardItem
    | CMSCollectionContentCard
    | CMSCollectionContentCardV2,
): item is CMSProductCardItem => item._type === 'productCard'

export const isCollectionCard = (
  item:
    | CMSProductCardItem
    | CMSCollectionContentCard
    | CMSCollectionContentCardV2,
): item is CMSCollectionContentCard | CMSCollectionContentCardV2 =>
  item._type === 'component.collectionSplitCard'

export const getAppliedFiltersFromSearchParams = ({
  searchParams,
  filters,
}: {
  searchParams: Record<string, string>
  filters?: CMSCollectionFilterGroup[]
}) => {
  const filtersParamString = searchParams['filters']
  if (!filtersParamString || !filters) return {}

  const filterParams = Object.fromEntries(
    filtersParamString?.split(';').map((filter) => {
      const [filterGroupId, filterId] = filter.split(':')
      return [filterGroupId, filterId]
    }, {}),
  )

  const appliedFilters = filters.reduce((acc, filterGroup) => {
    const appliedFilterId = filterParams[filterGroup.title]
    if (!appliedFilterId) return acc

    const appliedFilters = filterGroup.items.find(
      (item) => appliedFilterId === item.label,
    )
    return { ...acc, [filterGroup._id]: appliedFilters }
  }, {})

  return appliedFilters
}

export const getAppliedSortingFromSearchParams = ({
  searchParams,
  sortby,
}: {
  searchParams: Record<string, string>
  sortby?: CMSCollectionSortBy
}) => {
  const sortParamString = searchParams['sort']
  if (!sortParamString || !sortby) return undefined

  const defaultSorting = sortby?.items.find(
    (item) => 'value' in item && item.value === 'featured',
  )
  const [sortType, sortValue] = sortParamString.split(':')
  if (!sortType || !sortValue) return defaultSorting

  switch (sortType) {
    case 'attribute':
      return (
        sortby.items.find(
          (item) =>
            item._type === 'component.productAttribute' &&
            item.label === sortValue,
        ) || defaultSorting
      )
    case 'option':
      return (
        sortby.items.find(
          (item) =>
            item._type === 'component.sortingOption' &&
            item.value === sortValue,
        ) || defaultSorting
      )
    default:
      return defaultSorting
  }
}

export const updateSearchParams = ({
  filters,
  appliedFilters,
  appliedSorting,
}: {
  filters?: CMSCollectionFilterGroup[]
  appliedFilters: Record<string, CMSCollectionFilter>
  appliedSorting: CMSSortBy | undefined
}) => {
  const filterParams = Object.entries(appliedFilters)
    .map(([filterGroupId, filter]) => {
      if (!filter) return null
      return `${filters?.find(
        (filterGroup) => filterGroup._id === filterGroupId,
      )?.title}:${filter.label}`
    })
    .filter(Boolean)
    .join(';')

  let sortParams: string | undefined
  if (appliedSorting?._type === 'component.productAttribute') {
    sortParams = `attribute:${appliedSorting.label}`
  } else if (appliedSorting?._type === 'component.sortingOption') {
    if (appliedSorting.value !== 'featured') {
      sortParams = `option:${appliedSorting.value}`
    }
  }

  const searchParams = new URLSearchParams(window.location.search)
  if (filterParams) {
    searchParams.set('filters', filterParams)
  } else {
    searchParams.delete('filters')
  }
  if (sortParams) {
    searchParams.set('sort', sortParams)
  } else {
    searchParams.delete('sort')
  }

  window.history.replaceState(
    {},
    '',
    searchParams.size
      ? `${window.location.pathname}?${searchParams}`
      : window.location.pathname,
  )
}

export const filterGridItems = ({
  gridItems,
  appliedFilters,
}: {
  gridItems: CMSCollectionGridItem[]
  appliedFilters: Record<string, CMSCollectionFilter>
}) => {
  const filterAttributes = Object.values(appliedFilters).filter(Boolean)
  if (!filterAttributes.length) return gridItems

  return gridItems?.filter(({ item }) => {
    if (!isActiveProductCard) return false
    const { attributes } = item.card as CMSProductCardItem

    return filterAttributes.every(
      (filter) => attributes?.some(({ _id }) => _id === filter._id),
    )
  })
}

export const sortGridItems = ({
  gridItems,
  appliedSorting,
}: {
  gridItems: CMSCollectionGridItem[]
  appliedSorting: CMSSortBy
}) => {
  if (!gridItems) return []

  if (appliedSorting?._type === 'component.sortingOption') {
    const { value } = appliedSorting
    if (value === 'featured') {
      return gridItems
    } else if (value === 'price_asc') {
      return [...gridItems].sort((a, b) => {
        if (a.item.card._type !== 'productCard') return 1
        if (b.item.card._type !== 'productCard') return -1

        const priceA = getGridItemPrice(a)
        const priceB = getGridItemPrice(b)
        return priceA - priceB
      })
    } else if (value === 'price_desc') {
      return [...gridItems].sort((a, b) => {
        if (a.item.card._type !== 'productCard') return 1
        if (b.item.card._type !== 'productCard') return -1

        const priceA = getGridItemPrice(a)
        const priceB = getGridItemPrice(b)
        return priceB - priceA
      })
    }
  } else if (appliedSorting?._type === 'component.productAttribute') {
    let itemsWithProperty: CMSCollectionGridItem[] = []
    let itemsWithoutProperty: CMSCollectionGridItem[] = []

    gridItems.forEach((gridItem) => {
      const { card } = gridItem.item
      const hasProperty =
        'attributes' in card &&
        card.attributes?.some((attr) => attr._id === appliedSorting._id)
      if (hasProperty) {
        itemsWithProperty.push(gridItem)
      } else {
        itemsWithoutProperty.push(gridItem)
      }
    })

    return [...itemsWithProperty, ...itemsWithoutProperty]
  }
}

export const getAvailableFilters = ({
  gridItems,
  filters,
  appliedFilters,
}: {
  gridItems: CMSCollectionGridItem[]
  filters?: CMSCollectionFilterGroup[]
  appliedFilters: Record<string, CMSCollectionFilter>
}) => {
  return (filters || []).reduce((acc, filterGroup) => {
    const { [filterGroup._id]: _, ...otherAppliedFilters } = appliedFilters
    const filteredGridItems = filterGridItems({
      gridItems,
      appliedFilters: otherAppliedFilters,
    })

    const filterGroupAttributes = filterGroup.items.filter((item) => {
      return filteredGridItems?.some((gridItem) => {
        const productCard = gridItem.item.card as CMSProductCardItem
        return productCard?.attributes?.some((attr) => attr._id === item._id)
      })
    })
    return { ...acc, [filterGroup._id]: filterGroupAttributes }
  }, {})
}

export const getGridItemPrice = (gridItem: CMSCollectionGridItem) => {
  const productCard = gridItem.item.card as CMSProductCardItem
  return Math.min(
    ...productCard.variants
      .filter(isProductCardVariantEnabled)
      .map((variant) => variant.price),
  )
}
